import React from 'react';
import { Link } from 'react-router-dom';
import { AppBar, Tabs, Tab, Icon } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import TabContainer from 'global/components/TabContainer';
import ReportAssessmentInfo from '../AssessmentInfo';
import ReportAssessmentScoreContent from '../AssessmentScore';
import Highlights from '../Highlights';
import ReportAssessmentPhotos from '../Assessment/ReportAssessmentPhotos';
import { get, size } from 'global/lib/lodash';
import { useScreen } from 'global/styles';
import useStyles from './ReportTabs.styles';
import { PrintView } from 'global/components/print';
import SummaryScores from '../SummaryScores';
import MultiAssessment from '../AssessmentScore/MultiAssessment';
import { _size } from 'global/utils';
import StatusBar from '../StatusBar';

const tabs = [
  {
    value: 'info',
    label: 'Info',
    Component: ReportAssessmentInfo,
  },
  {
    value: 'scores',
    label: 'Scores',
    Component: ReportAssessmentScoreContent,
    className: 'noPad',
    canShow: (props) => {
      const { assessment } = props;
      const status = get(assessment, 'data.status');
      const scores = get(assessment, 'data.scores', {});
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      const nascores = Object.keys(scores).filter((k) => {
        const { calculatedScore } = scores[k];
        return !calculatedScore;
      });

      let hasScores = _size(nascores) < _size(scores);

      if (!hasScores && (status === 'review' || status === 'published')) {
        return false;
      }

      return true;
    },
  },
  {
    value: 'highlights',
    label: 'Highlights',
    Component: Highlights,
    canShow: (props) => {
      const { assessment, highlights, isTemplate, printView } = props;
      const hasHighlights = size(highlights.data);
      const status = get(assessment, 'data.status');
      const isLot = get(assessment, 'data.realm.tier') === 'lot';
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      if (isTemplate) {
        return false;
      }

      if (isLot) {
        return false;
      } else if (!hasHighlights && (status === 'review' || status === 'published' || printView)) {
        return false;
      }

      return true;
    },
  },
  {
    value: 'photos',
    label: 'Photos',
    Component: ReportAssessmentPhotos,
    canShow: (props) => {
      const { assessment, isTemplate } = props;
      const status = get(assessment, 'data.status');
      const photosCount = get(assessment, 'data.realmPhotoCount', 0);
      const loading = assessment.loading;
      if (loading) {
        return false;
      }

      let hasPhotos = photosCount > 0;

      if (isTemplate) {
        return false;
      }

      if (!hasPhotos && (status === 'review' || status === 'published')) {
        return false;
      }

      return true;
    },
  },
];

const printTabs = [
  tabs[0], //Info Page
  {
    Component: SummaryScores,
    pagebreak: false,
    canShow: (props) => {
      //TODO: Update the Scorecard summary to not display if there's only one scorecard and one system
      // const { assessment } = props;
      return true;
    },
  },
  tabs[2], //Highlights
  { Component: MultiAssessment },
];

export default function ReportTabs(props) {
  const classes = useStyles();
  const { page, currentTabValue, assessment, canShowScores } = props;

  const loading = get(assessment, 'loading', false);

  return (
    <div className={classes.root}>
      <AppBar position="static">
        <Tabs value={loading ? 'info' : currentTabValue} className={classes.tab} variant="scrollable" aria-label="scrollable auto tabs">
          {tabs.map((item, index) => {
            const canShow = item.canShow ? item.canShow(props) : true;
            if (!canShow) {
              return null;
            }
            return <Tab component={Link} to={`${page}/${item.value}`} label={item.label} value={item.value} key={item.value} aria-label={item.value} />;
          })}
        </Tabs>
        <PrintView
          items={printTabs
            .filter((tab) => (tab.canShow ? tab.canShow({ printView: true, ...props }) : true))
            .map((tab, i) => {
              return {
                title: i === 0 ? get(props, `assessment.data.name`, '') : undefined,
                titleStyle: { fontSize: 30, paddingTop: '16px', fontWeight: 'bold' },
                render: (rprops) => <tab.Component {...props} {...(rprops || {})} printView={true} />,
              };
            })}
          assessment={props.assessment.data}
          useLoaded={true}
          hideCoverPage={true}
          buttonContainerStyle={{}}
          onOpen={() => get(props, 'scores.update', () => 0)()}
          printBtn={{ text: <><PrintIcon className={classes.onlyMobile} /><div className={classes.hideMobile}>Print Report</div></> }}
          exportBtn={{ disabled: !canShowScores, text: <><Icon className={classes.onlyMobile}>insert_chart</Icon><div className={classes.hideMobile}>Download Data</div></> }}
        />
      </AppBar>
      {tabs.map((item) => {
        const TabComponent = item.Component;
        const className = get(classes, get(item, 'className'));
        return (
          currentTabValue === item.value && (
            <TabContainer className={className} key={item.value}>
              <StatusBar {...props} />
              <TabComponent {...props} className={classes.content} />
            </TabContainer>
          )
        );
      })}
    </div>
  );
}
